import CalendarIcon from "assets/images/Calendar";
import ClockIcon from "assets/images/Clock";
import CloseIcon from "assets/images/CloseIcon";
import classNames from "classnames";
import {format} from "date-fns";
import {useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  common as stringsCommon,
  confirmAppointmentModal as strings,
} from "strings";

import AlertModal from "components/AlertModal";
import Button from "components/Button";
import Divider from "components/Divider";
import PageTitle from "components/PageTitle";
import StyledModal from "components/StyledModal";

import * as models from "liberty-core/api/models";
import {useCreateAppointment} from "liberty-core/api/react-query/appointment";

import {useAuthenticatedUser} from "lib/auth";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./ConfirmAppointmentModal.module.css";

export default function ConfirmAppointmentModal(props: {
  visible: boolean;
  onClose: () => void;
  guestInfo?: models.AppointmentGuestInfo;
  selectedPatientId: string | null;
  campaignName: string;
  selectedCampaignOptionId: string;
  correlationId: string;
  startDateTime: string | undefined;
  responses: models.AppointmentQuestionUserAnswer[] | null;
  insuranceInfo?: {frontImage: File; backImage: File};
}) {
  const {mutateAsync: createAppointment, isLoading} = useCreateAppointment();

  const [pharmacy] = useCurrentPharmacy();
  const [authenticatedUser, loadingUser] = useAuthenticatedUser();
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();

  let patientName = "";
  if (authenticatedUser) {
    if (props.selectedPatientId) {
      const selectedPatient = authenticatedUser.data.linkedPatients?.find(
        (p) => p.patientId === props.selectedPatientId,
      );
      patientName = selectedPatient
        ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
        : "";
    } else {
      patientName = authenticatedUser.data.name;
    }
  } else {
    patientName = props.guestInfo
      ? `${props.guestInfo.firstName} ${props.guestInfo.lastName}`
      : "";
  }

  return (
    <StyledModal visible={props.visible} className={styles.modal}>
      <div className={styles.container}>
        <div className={styles.detailsContainer}>
          <div className={styles.section}>
            <PageTitle className={styles.header}>{strings.title}</PageTitle>
            <button
              className={styles.close}
              onClick={() => {
                return props.onClose();
              }}
            >
              <CloseIcon />
            </button>
          </div>
          <div className={styles.section}>
            <p className={styles.bold16}>{patientName}</p>
            <p className={styles.bold16}>{props.campaignName}</p>
            <p className={classNames(styles.regular16, styles.paddingBottom)}>
              {pharmacy ? pharmacy.name : ""}
            </p>
          </div>
          <Divider className={styles.divider} />
          <div className={styles.section}>
            <div className={styles.row}>
              <div className={styles.padding}>
                <CalendarIcon />
              </div>
              <p className={classNames(styles.bold16, styles.flex)}>
                {strings.date}
              </p>
              <p className={styles.regular16}>
                {props.startDateTime &&
                  format(new Date(props.startDateTime), "MM/dd/yy")}
              </p>
            </div>
            <Divider className={styles.divider} />
            <div className={styles.row}>
              <div className={styles.padding}>
                <ClockIcon />
              </div>
              <p className={classNames(styles.bold16, styles.flex)}>
                {strings.time}
              </p>
              <p className={styles.regular16}>
                {props.startDateTime &&
                  format(new Date(props.startDateTime), "hh:mm aaa")}
              </p>
            </div>
          </div>
          <Divider className={styles.divider} />
        </div>
        <Button
          loading={isLoading}
          onClick={() => {
            createAppointment({
              pharmacyId: pharmacy?.id ?? 0,
              correlationId: props.correlationId,
              campaignOptionId: props.selectedCampaignOptionId,
              startTime: props.startDateTime ?? "",
              patientId:
                authenticatedUser && !props.selectedPatientId
                  ? authenticatedUser.data.patientId
                  : props.selectedPatientId,
              guestFirstName: props.guestInfo?.firstName ?? null,
              guestLastName: props.guestInfo?.lastName ?? null,
              guestDOB: props.guestInfo?.dob ?? null,
              guestPhoneNum: props.guestInfo?.phone ?? null,
              guestEmail: props.guestInfo?.email ?? null,
              guestStreetAddress: props.guestInfo?.street ?? null,
              guestCity: props.guestInfo?.city ?? null,
              guestState: props.guestInfo?.state ?? null,
              guestZip: props.guestInfo?.zip ?? null,
              responses: props.responses ?? [],
              guestInsuranceCardFrontImage:
                props.insuranceInfo?.frontImage ?? null,
              guestInsuranceCardBackImage:
                props.insuranceInfo?.backImage ?? null,
            })
              .then((successMessage) => {
                props.onClose();
                navigate("/appointments", {
                  state: {startDateTime: props.startDateTime, successMessage},
                });
              })
              .catch((err) => {
                setErrorMessage(
                  err?.response?.data?.generic?.join("\n") ??
                    strings.genericErrorMessage,
                );
              });
          }}
          disabled={loadingUser}
        >
          {strings.confirmAndBook}
        </Button>
        <Button className={styles.cancel} variant="minimal">
          {stringsCommon.cancel}
        </Button>
        <AlertModal
          visible={!!errorMessage}
          title={stringsCommon.whoops}
          message={errorMessage}
          onClickConfirm={() => {
            setErrorMessage("");
          }}
        />
      </div>
    </StyledModal>
  );
}
